import React, { createContext, useContext, useState, useEffect } from "react";

const QuoteCartContext = createContext();

export const useQuoteCart = () => {
  const context = useContext(QuoteCartContext);
  if (!context) {
    throw new Error("useQuoteCart must be used within a QuoteCartProvider");
  }
  return context;
};

const STORAGE_KEY = "lohamart_quote_cart";

// A quote-cart item is one product+size line queued up to be requested
// together with other products in a single multi-product quote.
const getInitialItems = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (error) {
    console.error("Error loading quote cart:", error);
  }
  return [];
};

const makeKey = (item) => `${item.product_id}-${item.size_id || "nosize"}`;

export const QuoteCartProvider = ({ children }) => {
  const [quoteCartItems, setQuoteCartItems] = useState(getInitialItems);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(quoteCartItems));
    } catch (error) {
      console.error("Error saving quote cart:", error);
    }
  }, [quoteCartItems]);

  // Add one or more lines at once (e.g. several sizes of the same product).
  // Lines with the same product_id + size_id merge quantities instead of duplicating.
  const addToQuoteCart = (items) => {
    const incoming = Array.isArray(items) ? items : [items];
    setQuoteCartItems((prev) => {
      const next = [...prev];
      incoming.forEach((item) => {
        const key = makeKey(item);
        const existingIdx = next.findIndex((i) => makeKey(i) === key);
        if (existingIdx !== -1) {
          next[existingIdx] = {
            ...next[existingIdx],
            quantity:
              (parseFloat(next[existingIdx].quantity) || 0) +
              (parseFloat(item.quantity) || 0),
          };
        } else {
          next.push({ ...item, key });
        }
      });
      return next;
    });
  };

  const removeFromQuoteCart = (key) => {
    setQuoteCartItems((prev) => prev.filter((item) => item.key !== key));
  };

  const updateQuoteQuantity = (key, quantity) => {
    if (quantity <= 0) {
      removeFromQuoteCart(key);
      return;
    }
    setQuoteCartItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, quantity } : item)),
    );
  };

  const clearQuoteCart = () => {
    setQuoteCartItems([]);
  };

  const getQuoteCartCount = () => quoteCartItems.length;

  const isProductInQuoteCart = (productId) =>
    quoteCartItems.some((item) => item.product_id === productId);

  const value = {
    quoteCartItems,
    addToQuoteCart,
    removeFromQuoteCart,
    updateQuoteQuantity,
    clearQuoteCart,
    getQuoteCartCount,
    isProductInQuoteCart,
  };

  return (
    <QuoteCartContext.Provider value={value}>
      {children}
    </QuoteCartContext.Provider>
  );
};

export default QuoteCartContext;
